import PropTypes from "prop-types"
import React, { Component } from "react"

//Simple bar
import SimpleBar from "simplebar-react"

// MetisMenu
import MetisMenu from "metismenujs"
import { withRouter } from "react-router-dom"
import { Link } from "react-router-dom"

//i18n
import { withTranslation } from "react-i18next"
import { hasPermission } from "helpers/checkPermission"

const isPreConstructionPath = (path = "") => {
  const p = String(path).toLowerCase()
  return (
    p.startsWith("/pre-construction") ||
    p.startsWith("/landing-page")
  )
}

/** Project workspace pages that render ProjectSidebar (dashboard, setup, etc.) */
const isProjectWorkspacePath = (path = "") => {
  const p = String(path).toLowerCase()
  return (
    /\/pre-construction\/campaign(\/|$)/.test(p) ||
    /\/pre-construction\/create-project(\/|$)/.test(p)
  )
}

class SidebarContent extends Component {
  constructor(props) {
    super(props)
    this.refDiv = React.createRef()
    this.metisMenu = null
  }

  componentDidMount() {
    this.initMenu()
    this._collapsedObserver = new MutationObserver(() => {
      const collapsed = this.isSidebarCollapsed()
      if (collapsed !== this._lastCollapsed) {
        this._lastCollapsed = collapsed
        this.initMenu()
      }
    })
    this._lastCollapsed = this.isSidebarCollapsed()
    this._collapsedObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    })
  }

  componentWillUnmount() {
    this.clearFlyoutTimers()
    if (this._collapsedObserver) {
      this._collapsedObserver.disconnect()
      this._collapsedObserver = null
    }
    if (this.metisMenu && typeof this.metisMenu.dispose === "function") {
      this.metisMenu.dispose()
      this.metisMenu = null
    }
  }

  // eslint-disable-next-line no-unused-vars
  componentDidUpdate(prevProps, prevState, ss) {
    if (
      this.props.type !== prevProps.type ||
      this.props.location.pathname !== prevProps.location.pathname
    ) {
      this.initMenu()
    }
  }

  openSubmenu = submenu => {
    if (!submenu) return
    submenu.classList.add("mm-show")
    submenu.classList.remove("mm-collapsing")
    submenu.style.height = "auto"
    submenu.style.maxHeight = "none"
    submenu.style.overflow = "visible"
    submenu.style.display = "block"
    submenu.setAttribute("aria-expanded", "true")
  }

  closeSubmenu = submenu => {
    if (!submenu) return
    submenu.classList.remove("mm-show")
    submenu.classList.remove("mm-collapsing")
    submenu.style.height = ""
    submenu.style.maxHeight = ""
    submenu.style.overflow = ""
    submenu.style.display = ""
    submenu.setAttribute("aria-expanded", "false")
  }

  isSidebarCollapsed = () =>
    typeof document !== "undefined" &&
    document.body.classList.contains("vertical-collpsed")

  clearFlyoutTimers = () => {
    if (this._flyoutCloseTimers) {
      Object.keys(this._flyoutCloseTimers).forEach(id => {
        clearTimeout(this._flyoutCloseTimers[id])
      })
    }
    this._flyoutCloseTimers = {}
  }

  bindCollapsedFlyoutHandlers = ul => {
    if (!ul) return
    this.clearFlyoutTimers()

    ul.querySelectorAll(":scope > li").forEach(li => {
      const submenu = li.querySelector(":scope > ul.sub-menu")
      if (!submenu) return
      const key = submenu.id || li.id || String(Math.random())

      // Clear prior handlers so re-init does not stack listeners
      li.onmouseenter = null
      li.onmouseleave = null

      li.onmouseenter = () => {
        if (!this.isSidebarCollapsed()) {
          li.classList.remove("is-flyout-open")
          return
        }
        if (this._flyoutCloseTimers?.[key]) {
          clearTimeout(this._flyoutCloseTimers[key])
          this._flyoutCloseTimers[key] = null
        }
        // Close other flyouts
        ul.querySelectorAll(":scope > li.is-flyout-open").forEach(other => {
          if (other !== li) {
            other.classList.remove("is-flyout-open")
            const otherSub = other.querySelector(":scope > ul.sub-menu")
            if (otherSub) this.closeSubmenu(otherSub)
          }
        })
        li.classList.add("is-flyout-open")
        this.openSubmenu(submenu)
      }

      li.onmouseleave = () => {
        if (!this.isSidebarCollapsed()) {
          li.classList.remove("is-flyout-open")
          return
        }
        if (!this._flyoutCloseTimers) this._flyoutCloseTimers = {}
        if (this._flyoutCloseTimers[key]) clearTimeout(this._flyoutCloseTimers[key])
        this._flyoutCloseTimers[key] = setTimeout(() => {
          li.classList.remove("is-flyout-open")
          this.closeSubmenu(submenu)
          this._flyoutCloseTimers[key] = null
        }, 180)
      }
    })
  }

  initMenu() {
    if (this.metisMenu && typeof this.metisMenu.dispose === "function") {
      this.metisMenu.dispose()
      this.metisMenu = null
    }

    const ul = document.getElementById("side2-menu")
    if (!ul) return

    this.resetMenuState(ul)
    ul.querySelectorAll(".is-flyout-open").forEach(node =>
      node.classList.remove("is-flyout-open")
    )

    const pathname = this.props.location?.pathname || ""
    const collapsed = this.isSidebarCollapsed()

    const isPreConstructionRoute = isPreConstructionPath(pathname)
    const isWorkspace = isProjectWorkspacePath(pathname)

    if (isPreConstructionRoute) {
      const preConstructionItem = document.getElementById("preconstruction-menu-item")
      const preConstructionLink = document.getElementById("preconstruction-menu-link")
      const preConstructionSubmenu = document.getElementById("preconstruction-submenu")

      if (preConstructionItem) preConstructionItem.classList.add("mm-active")
      if (preConstructionLink) {
        preConstructionLink.classList.add("mm-active")
        preConstructionLink.classList.add("active")
      }
      // Expanded sidebar only: auto-open submenu on list routes
      if (preConstructionSubmenu && !isWorkspace && !collapsed) {
        this.openSubmenu(preConstructionSubmenu)
      }
    }

    this.applySectionMenuActive(pathname)

    let matchingMenuItem = null
    const items = ul.getElementsByTagName("a")
    for (let i = 0; i < items.length; ++i) {
      const isToggleLink =
        items[i].classList.contains("has-arrow") ||
        items[i].getAttribute("href") === "#" ||
        items[i].getAttribute("href") === "/#"
      if (isToggleLink) {
        continue
      }

      if (this.props.location.pathname.toLowerCase() === items[i].pathname.toLowerCase()) {
        matchingMenuItem = items[i]
        break
      }
    }
    if (matchingMenuItem) {
      this.activateParentDropdown(matchingMenuItem)
    }

    // MetisMenu fights collapsed flyout clicks — only use it when expanded
    if (!collapsed) {
      this.metisMenu = new MetisMenu("#side2-menu")
      this.clearSubmenuInlineStyles(ul)
      if (isPreConstructionRoute && !isWorkspace) {
        this.openSubmenu(document.getElementById("preconstruction-submenu"))
      }
      const campaignSub = document.getElementById("campaign-submenu")
      if (
        campaignSub &&
        (pathname.startsWith("/campaign") || pathname.startsWith("/all-templates"))
      ) {
        this.openSubmenu(campaignSub)
      }
    } else {
      this.clearSubmenuInlineStyles(ul)
    }

    this.bindCollapsedFlyoutHandlers(ul)
  }

  clearSubmenuInlineStyles = ul => {
    if (!ul) return
    ul.querySelectorAll("ul.sub-menu").forEach(node => {
      node.style.height = ""
      node.style.overflow = ""
      node.classList.remove("mm-collapsing")
    })
  }

  applySectionMenuActive = (pathname = "") => {
    const collapsed = this.isSidebarCollapsed()
    const sections = [
      {
        id: "dashboard-menu-item",
        linkId: "dashboard-menu-link",
        match: () => pathname === "/dashboard" || pathname === "/",
      },
      {
        id: "office-menu-item",
        linkId: "office-menu-link",
        match: () => pathname.startsWith("/office"),
      },
      {
        id: "leads-menu-item",
        linkId: "leads-menu-link",
        match: () => pathname.startsWith("/lead"),
      },
      {
        id: "contacts-menu-item",
        linkId: "contacts-menu-link",
        match: () =>
          pathname.startsWith("/all-contacts") || pathname.startsWith("/contact"),
      },
      {
        id: "clients-menu-item",
        linkId: "clients-menu-link",
        match: () => pathname.startsWith("/clients"),
      },
      {
        id: "campaign-menu-item",
        linkId: "campaign-menu-link",
        submenuId: "campaign-submenu",
        match: () =>
          pathname.startsWith("/campaign") ||
          pathname.startsWith("/all-templates"),
      },
      {
        id: "social-menu-item",
        linkId: "social-menu-link",
        match: () => pathname.startsWith("/social"),
      },
      {
        id: "inbox-menu-item",
        linkId: "inbox-menu-link",
        match: () =>
          pathname.startsWith("/email-inbox") ||
          pathname.startsWith("/email-sent") ||
          pathname.startsWith("/email-read") ||
          pathname.startsWith("/email-assign") ||
          pathname.startsWith("/email"),
      },
      {
        id: "settings-menu-item",
        linkId: "settings-menu-link",
        match: () =>
          pathname.startsWith("/setting") || pathname.startsWith("/collection"),
      },
    ]

    sections.forEach(section => {
      if (!section.match()) return
      const item = document.getElementById(section.id)
      const link = document.getElementById(section.linkId)
      if (item) item.classList.add("mm-active")
      if (link) {
        link.classList.add("mm-active")
        link.classList.add("active")
      }
      // Don't force-open accordion submenu while sidebar is collapsed
      if (section.submenuId && !collapsed) {
        const submenu = document.getElementById(section.submenuId)
        if (submenu) this.openSubmenu(submenu)
      }
    })
  }

  resetMenuState = ul => {
    ul.querySelectorAll(".mm-active").forEach(node => node.classList.remove("mm-active"))
    ul.querySelectorAll(".active").forEach(node => node.classList.remove("active"))
    ul.querySelectorAll(".mm-show").forEach(node => node.classList.remove("mm-show"))
    ul.querySelectorAll(".mm-collapsing").forEach(node => node.classList.remove("mm-collapsing"))
    ul.querySelectorAll("ul.sub-menu").forEach(node => {
      node.style.height = ""
      node.setAttribute("aria-expanded", "false")
    })
  }

  scrollElement = item => {
    setTimeout(() => {
      if (this.refDiv.current !== null) {
        if (item) {
          const currentPosition = item.offsetTop
          if (currentPosition > window.innerHeight) {
            if (this.refDiv.current)
              this.refDiv.current.getScrollElement().scrollTop =
                currentPosition - 300
          }
        }
      }
    }, 300)
  }

  activateParentDropdown = item => {
    item.classList.add("active")
    const parent = item.parentElement
    const collapsed = this.isSidebarCollapsed()

    const parent2El = parent.childNodes[1]
    if (parent2El && parent2El.id !== "side2-menu" && !collapsed) {
      parent2El.classList.add("mm-show")
    }

    if (parent) {
      parent.classList.add("mm-active")
      const parent2 = parent.parentElement

      if (parent2) {
        if (!collapsed) parent2.classList.add("mm-show") // ul tag

        const parent3 = parent2.parentElement // li tag

        if (parent3) {
          parent3.classList.add("mm-active") // li
          parent3.childNodes[0].classList.add("mm-active") //a
          const parent4 = parent3.parentElement // ul
          if (parent4) {
            if (!collapsed) parent4.classList.add("mm-show") // ul
            const parent5 = parent4.parentElement
            if (parent5) {
              if (!collapsed) parent5.classList.add("mm-show") // li
              parent5.childNodes[0].classList.add("mm-active") // a tag
            }
          }
        }
      }
      this.scrollElement(item)
      return false
    }
    this.scrollElement(item)
    return false
  }

  handleParentMenuClick = e => {
    // Collapsed rail uses hover flyout only
    if (this.isSidebarCollapsed()) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    e.preventDefault()
    e.stopPropagation()
    const link = e.currentTarget
    const li = link?.parentElement
    const submenu = li?.querySelector(":scope > ul.sub-menu")
    if (!submenu) return

    const willOpen = !submenu.classList.contains("mm-show")
    const root = document.getElementById("side2-menu")
    if (willOpen && root) {
      root.querySelectorAll(":scope > li > ul.sub-menu.mm-show").forEach(node => {
        if (node !== submenu) this.closeSubmenu(node)
      })
      this.openSubmenu(submenu)
    } else {
      this.closeSubmenu(submenu)
    }
  }

  handleSubmenuNav = to => e => {
    e.preventDefault()
    e.stopPropagation()
    const path = String(to || "")
    if (!path || path === "#") return
    const { history } = this.props
    if (!history) return
    // Close flyout immediately so it doesn't linger after navigation
    if (this.isSidebarCollapsed()) {
      const li = e.currentTarget?.closest("li.is-flyout-open, #side2-menu > li")
      const parentLi = e.currentTarget?.closest("#side2-menu > li")
      if (parentLi) {
        parentLi.classList.remove("is-flyout-open")
        const sub = parentLi.querySelector(":scope > ul.sub-menu")
        if (sub) this.closeSubmenu(sub)
      }
    }
    if (history.location.pathname.toLowerCase() !== path.toLowerCase()) {
      history.push(path)
    }
  }

  renderSubmenuLink = (to, label) => (
    <li>
      <Link to={to} onClick={this.handleSubmenuNav(to)}>
        {this.props.t(label)}
      </Link>
    </li>
  )

  renderSectionTitle = (label) => (
    <li className="menu-title sidebar-menu-section">{this.props.t(label)}</li>
  )

  render() {
    const { isResponsive } = this.props
    const pathname = this.props.location?.pathname || ""
    const isDashboardRoute =
      pathname === "/dashboard" || pathname === "/"
    const isOfficeRoute = pathname.startsWith("/office")
    const isLeadRoute = pathname.startsWith("/lead")
    const isContactRoute =
      pathname.startsWith("/all-contacts") ||
      pathname.startsWith("/contact")
    const isClientRoute = pathname.startsWith("/clients")
    const isPreConstructionRoute = isPreConstructionPath(pathname)
    const isCampaignRoute =
      pathname.startsWith("/campaign") ||
      pathname.startsWith("/all-templates")
    const isSocialRoute = pathname.startsWith("/social")
    const isInboxRoute =
      pathname.startsWith("/email-inbox") ||
      pathname.startsWith("/email-sent") ||
      pathname.startsWith("/email-read") ||
      pathname.startsWith("/email-assign") ||
      pathname.startsWith("/email")
    const isSettingsRoute =
      pathname.startsWith("/setting") || pathname.startsWith("/collection")

    const showPeopleSection =
      hasPermission("RealtorLeads", "View") ||
      hasPermission("RealtorContacts", "View") ||
      hasPermission("RealtorClients", "View")

    const showWorkSection =
      hasPermission("RealtorPreConsProjects", "Add") ||
      hasPermission("RealtorLeadsActivities", "View") ||
      hasPermission("RealtorPreConsProjects", ["View", "Edit", "Add"]) ||
      (!isResponsive && hasPermission("RealtorPreConsProjects", ["View", "Edit", "Add"]))

    return (
      <React.Fragment>
        <SimpleBar className="h-100 main-menu" ref={this.refDiv}>
          <div id="sidebar-menu">
            <ul className="metismenu list-unstyled sidebar-menu-categorized" id="side2-menu">
              {this.renderSectionTitle("Overview")}
              <li id="dashboard-menu-item" className={isDashboardRoute ? "mm-active" : ""}>
                <Link
                  id="dashboard-menu-link"
                  to="/dashboard"
                  className={isDashboardRoute ? "active mm-active" : ""}
                >
                  <i className="bx bx-grid-alt" />
                  <span>{this.props.t("Dashboard")}</span>
                </Link>
              </li>

              {showPeopleSection && this.renderSectionTitle("People")}

              {hasPermission("RealtorLeads", "View") && (
              <li id="leads-menu-item" className={isLeadRoute ? "mm-active" : ""}>
                <Link
                  id="leads-menu-link"
                  to="/lead/dashboard"
                  className={isLeadRoute ? "active mm-active" : ""}
                >
                  <i className="bx bx-user-plus" />
                  <span>{this.props.t("Leads")}</span>
                </Link>
              </li>
              )}

              {hasPermission("RealtorContacts", "View") && (
               <li id="contacts-menu-item" className={isContactRoute ? "mm-active" : ""}>
              <Link
                id="contacts-menu-link"
                to="/all-contacts"
                className={isContactRoute ? "active mm-active" : ""}
              >
                <i className="mdi mdi-account-details" />
                <span>{this.props.t("Contacts")}</span>
              </Link>
            </li>
              )}

             {hasPermission("RealtorClients", "View") && (
              <li id="clients-menu-item" className={isClientRoute ? "mm-active" : ""}>
                <Link
                  id="clients-menu-link"
                  to="/clients"
                  className={isClientRoute ? "active mm-active" : ""}
                >
                <i className="mdi mdi-account-heart" />
                  <span>{this.props.t("Clients")}</span>
                </Link>
              </li>
             )}

              {showWorkSection && this.renderSectionTitle("Work")}

              {hasPermission("RealtorPreConsProjects", "Add") && (
              <li>
                <Link to="/transaction">
                  <i className="bx bx-briefcase" />
                  <span>{this.props.t("Deals")}</span>
                </Link>
              </li>
              )}

              {hasPermission("RealtorLeadsActivities", "View") && (
            <li>
                <Link to="/activity" className="">
                  <i className="bx bx-pulse" />
                  <span>{this.props.t("Activities")}</span>
                </Link>
              </li>
              )}

              <li
                id="preconstruction-menu-item"
                className={isPreConstructionRoute ? "mm-active" : ""}
              >
                <Link to="#"
                id="preconstruction-menu-link"
                onClickCapture={this.handleParentMenuClick}
                className={`has-arrow ${isPreConstructionRoute ? "mm-active" : ""}`}>
                  <i className="bx bxs-building" />
                  <span>{this.props.t("Pre-construction")}</span>
                </Link>
                <ul
                  id="preconstruction-submenu"
                  className="sub-menu mm-collapse"
                >
                     {hasPermission("RealtorPreConsProjects", ["View", "Edit", "Add"]) &&
                    this.renderSubmenuLink("/pre-construction/my-project", "Projects")}
                   {hasPermission("RealtorPreConsBuilders", ["View", "Edit", "Add"]) &&
                    this.renderSubmenuLink("/pre-construction/builder", "Builders")}
                 {hasPermission("RealtorPreConsProjects", ["View", "Edit", "Add"]) &&
                    this.renderSubmenuLink("/pre-construction/project/lead", "Pre-construction leads")}
                   {hasPermission("RealtorPreConsProjects", ["View", "Edit", "Add"]) &&
                    this.renderSubmenuLink("/pre-construction/project/clients", "Pre-construction clients")}
                   {!isResponsive &&
  hasPermission("RealtorPreConsProjects", ["View", "Edit", "Add"]) &&
    this.renderSubmenuLink("/landing-page", "Landing page")}
                </ul>
              </li>

               {!isResponsive &&(
              <li
                id="campaign-menu-item"
                className={isCampaignRoute ? "mm-active" : ""}
              >
                <Link
                  id="campaign-menu-link"
                  to="/campaign"
                  className={isCampaignRoute ? "active mm-active" : ""}
                >
                  <i className="bx bxs-megaphone" />
                  <span>{this.props.t("Campaigns")}</span>
                </Link>
              </li>
               )}

             
        <li
                id="social-menu-item"
                className={isSocialRoute ? "mm-active" : ""}
              >
                <Link
                  id="social-menu-link"
                  to="/social/get-started"
                  className={isSocialRoute ? "active mm-active" : ""}
                >
                  <i className="bx bxl-instagram-alt" />
                  <span>{this.props.t("Social")}</span>
                </Link>
              </li>
              

              {this.renderSectionTitle("Office")}

             {hasPermission("RealtorOfficeEvents", "Edit") && (
                  <li id="office-menu-item" className={isOfficeRoute ? "mm-active" : ""}>
                    <Link
                      id="office-menu-link"
                      to="/office/events"
                      className={isOfficeRoute ? "active mm-active" : ""}
                    >
                      <i className="bx bxs-building-house" />
                      <span>{this.props.t("Inter-office")}</span>
                    </Link>
                  </li>
                )}

              <li id="inbox-menu-item" className={isInboxRoute ? "mm-active" : ""}>
                <Link
                  id="inbox-menu-link"
                  to="/email-inbox"
                  className={isInboxRoute ? "active mm-active" : ""}
                >
                  <i className="bx bx-envelope" />
                  <span>{this.props.t("Inbox")}</span>
                </Link>
              </li>

              <li id="settings-menu-item" className={isSettingsRoute ? "mm-active" : ""}>
                <Link
                  id="settings-menu-link"
                  to="/setting/agent-profile"
                  className={isSettingsRoute ? "active mm-active" : ""}
                >
                  <i className="bx bx-cog" />
                  <span>{this.props.t("Settings")}</span>
                </Link>
              </li>
            </ul>
          </div>
        </SimpleBar>
      </React.Fragment>
    )
  }
}

SidebarContent.propTypes = {
  history: PropTypes.object,
  location: PropTypes.object,
  t: PropTypes.any,
  type: PropTypes.string,
  match: PropTypes.object,
  isResponsive: PropTypes.object,
  userdetail: PropTypes.object,
}

export default withRouter(withTranslation()(SidebarContent))
